/* The history rail (board P, drawn as the P5 boards' quiet rail: `BuddyP5-1-start`, `BuddyP5b-1-start`,
 * owner, 4 November 2026): a 272px column on the page's own ground, „Rozmowy" and its count, „Nowa
 * rozmowa" as a plain row, the conversations grouped by age, and the retention note at its foot.
 * Regular weight throughout; the open conversation is a flat grey fill, never a card, and nothing
 * here is the accent. Every readable line is in the secondary ink: the tertiary one is not
 * readable text on these grounds. */

.rail {
  display: flex;
  flex: none;
  flex-direction: column;
  width: 272px;
  min-height: 0;
  padding: var(--space-3) var(--space-10px) var(--space-14px);
  background: var(--bg);
  border-right: var(--border-hairline) solid var(--border);
}

.head {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: space-between;
  height: 32px;
  padding: var(--space-0) var(--space-10px);
}

.title {
  margin: var(--space-0);
  font-size: var(--fs-body);
  font-weight: var(--fw-regular);
  line-height: var(--lh-home-body);
}

.count {
  font-size: var(--fs-foot);
  font-variant-numeric: tabular-nums;
  color: var(--text-2);
}

/* „Nowa rozmowa": a row like the conversations under it, not a raised button. */
.newConversation {
  display: flex;
  flex: none;
  gap: var(--space-10px);
  align-items: center;
  height: 32px;
  margin-top: var(--space-1);
  padding: var(--space-0) var(--space-10px);
  font-family: var(--font-body);
  font-size: var(--fs-foot);
  color: var(--text);
  text-align: left;
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: var(--radius-8px);
}

.newConversation:hover {
  background: var(--hover-tint);
}

.newConversation:active {
  background: var(--buddy-row-open);
}

.newConversation[aria-disabled='true'] {
  cursor: default;
  opacity: 0.4;
}

.newConversation[aria-disabled='true']:hover,
.newConversation[aria-disabled='true']:active {
  background: transparent;
}

.newGlyph {
  width: 16px;
  height: 16px;
  color: var(--text-2);
}

.list {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-22px);
  min-height: 0;
  margin-top: var(--space-22px);
}

.scroller {
  overflow-y: auto;
}

.group {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-1px);
}

/* The age, in sentence case: small, muted, not tracked caps. */
.groupTitle {
  margin: var(--space-0);
  padding: var(--space-0) var(--space-10px) var(--space-6px);
  font-size: var(--fs-caption);
  font-weight: var(--fw-regular);
  line-height: var(--lh-home-caption);
  color: var(--text-2);
}

.rows {
  display: flex;
  flex-direction: column;
  gap: var(--space-1px);
  padding: var(--space-0);
  margin: var(--space-0);
  list-style: none;
}

/* A conversation: its title and time on one line, and under it who it is for and what it waits for
 * (R13), muted. The same weight open or closed; only the fill says which one is open. The grid
 * draws the two lines from children read in the order title, who, when, what it waits for. */
.row {
  display: grid;
  grid-template-areas:
    "title title title time"
    "who dot waits waits";
  grid-template-columns: auto auto minmax(0, 1fr) auto;
  gap: var(--space-2px) var(--space-1);
  align-items: baseline;
  padding: var(--space-6px) var(--space-10px);
  color: var(--text-2);
  border-radius: var(--radius-8px);
}

.row:hover {
  color: var(--text);
  background: var(--hover-tint);
}

.rowOpen,
.rowOpen:hover {
  color: var(--text);
  background: var(--buddy-row-open);
}

.rowTitle {
  grid-area: title;
  min-width: 0;
  margin-right: var(--space-1);
  overflow: hidden;
  font-size: var(--fs-foot);
  line-height: var(--lh-home-foot);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rowTime {
  grid-area: time;
  font-size: var(--fs-label);
  line-height: var(--lh-home-label);
  font-variant-numeric: tabular-nums;
  color: var(--text-2);
}

.rowRecipient,
.rowSeparator,
.rowWaits {
  min-width: 0;
  overflow: hidden;
  font-size: var(--fs-label);
  line-height: var(--lh-home-label);
  color: var(--text-2);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rowRecipient {
  grid-area: who;
  max-width: 150px;
}

.rowSeparator {
  grid-area: dot;
}

.rowWaits {
  grid-area: waits;
}

/* „Pokaż starsze" and the failed list's retry: the one secondary button, a hairline pill. */
.more,
.noteAction {
  align-self: flex-start;
  height: 32px;
  padding: var(--space-0) var(--space-14px);
  font-family: var(--font-body);
  font-size: var(--fs-foot);
  font-weight: var(--fw-medium);
  color: var(--text);
  cursor: pointer;
  background: transparent;
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--r-pill);
}

.noteAction {
  align-self: center;
}

.more:hover,
.noteAction:hover {
  background: var(--hover-tint);
}

.more:active,
.noteAction:active {
  background: var(--buddy-row-open);
}

.more[aria-disabled='true'] {
  cursor: default;
  color: var(--text-2);
  background: transparent;
}

/* The failed and the empty states: centred in the list's own box, so the foot stays where it is. */
.note {
  align-items: center;
  justify-content: center;
  padding: var(--space-0) var(--space-2);
  text-align: center;
}

.noteText {
  margin: var(--space-0);
  font-size: var(--fs-foot);
  color: var(--text-2);
}

/* The wait: one group of the list's own shape — its head and rows at the loaded pitch. */
.skeletonHead {
  display: flex;
  align-items: center;
  height: 20px;
  padding: var(--space-0) var(--space-10px) var(--space-6px);
}

.skeletonHeadBar {
  width: 22%;
  height: 10px;
}

.skeletonRow {
  display: flex;
  flex-direction: column;
  gap: var(--space-2px);
  padding: var(--space-6px) var(--space-10px);
}

.skeletonLine,
.skeletonSub {
  display: flex;
  align-items: center;
}

.skeletonLine {
  height: 18px;
}

.skeletonSub {
  height: 16px;
}

.skeletonTitle {
  width: 72%;
  height: 11px;
}

.skeletonMeta {
  width: 46%;
  height: 10px;
}

.foot {
  flex: none;
  margin: var(--space-3) var(--space-0) var(--space-0);
  padding: var(--space-0) var(--space-10px);
  font-size: var(--fs-caption);
  line-height: var(--lh-home-caption);
  color: var(--text-2);
}

.newConversation:focus-visible,
.more:focus-visible,
.noteAction:focus-visible,
.scroller:focus-visible,
.row:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}
